import { useMutation, useQuery } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { GET_BANDS } from "../../components/BandPicker/graphql";
import { CREATE_BOOKING } from "../booking/graphql";
import { GET_ALL_STAGES } from "../stages/graphql";

interface AnnouncementBandRef {
  band?: {
    id?: string | null;
  } | null;
}

export interface BookingBuilderAnnouncement {
  id: string;
  name: string;
  bands: AnnouncementBandRef[];
}

interface AnnouncementBookingBuilderProps {
  announcements: BookingBuilderAnnouncement[];
  onBookingsCreated?: () => Promise<void> | void;
}

interface BandOption {
  id: string;
  name: string;
}

interface StageOption {
  id: string;
  name: string;
}

interface StagedBooking {
  id: string;
  announcementId: string;
  announcementName: string;
  bandId: string;
  bandName: string;
  dateKey: string | null;
  stageId: string | null;
  stageName: string | null;
}

interface StagedGroup {
  key: string;
  dateKey: string | null;
  stageId: string | null;
  stageName: string | null;
  bands: StagedBooking[];
}

const buildDatePayload = (dateKey: string | null) => {
  if (!dateKey) {
    return {
      date: null,
      startTime: null,
      endTime: null,
    };
  }

  const [yearRaw, monthRaw, dayRaw] = dateKey.split("/");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return {
      date: null,
      startTime: null,
      endTime: null,
    };
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  const midday = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  if (Number.isNaN(date.valueOf()) || Number.isNaN(midday.valueOf())) {
    return {
      date: null,
      startTime: null,
      endTime: null,
    };
  }

  return {
    date: date.toISOString(),
    startTime: midday.toISOString(),
    endTime: midday.toISOString(),
  };
};

const formatGroupHeading = (group: StagedGroup) => {
  const dateLabel = group.dateKey
    ? `${group.dateKey} ${dayjs(group.dateKey).format("dddd")}`
    : "No date selected";
  const stageLabel = group.stageName ? `"${group.stageName}"` : '"No stage selected"';
  return `${dateLabel} ${stageLabel}`;
};

export const BookingBuilder = ({
  announcements,
  onBookingsCreated,
}: AnnouncementBookingBuilderProps) => {
  const { data: bandsData, loading: loadingBands } = useQuery(GET_BANDS, {
    fetchPolicy: "cache-first",
  });
  const { data: stagesData } = useQuery(GET_ALL_STAGES, {
    fetchPolicy: "cache-first",
  });
  const [createBooking] = useMutation(CREATE_BOOKING);

  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState("");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBandIds, setSelectedBandIds] = useState<Set<string>>(new Set());
  const [stagedBookings, setStagedBookings] = useState<StagedBooking[]>([]);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [creatingBookings, setCreatingBookings] = useState(false);

  useEffect(() => {
    if (announcements.length === 0) {
      setSelectedAnnouncementId("");
      return;
    }

    if (!announcements.some((item) => item.id === selectedAnnouncementId)) {
      setSelectedAnnouncementId(announcements[0].id);
    }
  }, [announcements, selectedAnnouncementId]);

  useEffect(() => {
    setSelectedBandIds(new Set());
  }, [selectedAnnouncementId]);

  const allBands = useMemo<BandOption[]>(() => {
    return (bandsData?.bands ?? [])
      .filter(
        (band): band is { id: string; name: string } =>
          Boolean(band?.id) && Boolean(band?.name)
      )
      .map((band) => ({
        id: band.id,
        name: band.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [bandsData?.bands]);

  const stageOptions = useMemo<StageOption[]>(() => {
    return (stagesData?.stages ?? []).reduce<StageOption[]>((acc, stage) => {
      if (!stage?.id || !stage?.name) return acc;

      acc.push({
        id: stage.id,
        name: stage.name,
      });

      return acc;
    }, []);
  }, [stagesData?.stages]);

  const selectedAnnouncement = useMemo(
    () => announcements.find((item) => item.id === selectedAnnouncementId) ?? null,
    [announcements, selectedAnnouncementId]
  );

  const existingBandIds = useMemo(() => {
    const ids = new Set<string>();

    (selectedAnnouncement?.bands ?? []).forEach((booking) => {
      const bandId = booking?.band?.id;
      if (!bandId) return;
      ids.add(bandId);
    });

    return ids;
  }, [selectedAnnouncement]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredBands = useMemo(() => {
    if (!normalizedSearchTerm) return allBands;

    return allBands.filter((band) =>
      band.name.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [allBands, normalizedSearchTerm]);

  const selectedBands = useMemo(
    () => allBands.filter((band) => selectedBandIds.has(band.id)),
    [allBands, selectedBandIds]
  );

  const selectedStageName = useMemo(
    () => stageOptions.find((stage) => stage.id === selectedStageId)?.name ?? null,
    [stageOptions, selectedStageId]
  );

  const visibleStagedBookings = useMemo(
    () =>
      stagedBookings.filter((item) => item.announcementId === selectedAnnouncementId),
    [selectedAnnouncementId, stagedBookings]
  );

  const groupedStagedBookings = useMemo<StagedGroup[]>(() => {
    const grouped = new Map<string, StagedGroup>();

    visibleStagedBookings.forEach((item) => {
      const key = `${item.dateKey ?? "__NO_DATE__"}|${item.stageId ?? "__NO_STAGE__"}`;
      const current = grouped.get(key);

      if (current) {
        current.bands.push(item);
        return;
      }

      grouped.set(key, {
        key,
        dateKey: item.dateKey,
        stageId: item.stageId,
        stageName: item.stageName,
        bands: [item],
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aDate = a.dateKey ? dayjs(a.dateKey) : null;
      const bDate = b.dateKey ? dayjs(b.dateKey) : null;
      const aValue = aDate?.isValid() ? aDate.valueOf() : Number.MAX_SAFE_INTEGER;
      const bValue = bDate?.isValid() ? bDate.valueOf() : Number.MAX_SAFE_INTEGER;

      if (aValue === bValue) {
        const aStage = a.stageName ?? "No stage";
        const bStage = b.stageName ?? "No stage";
        return aStage.localeCompare(bStage);
      }

      return aValue - bValue;
    });
  }, [visibleStagedBookings]);

  const stagedCombinationKeySet = useMemo(
    () =>
      new Set(
        visibleStagedBookings.map(
          (item) => `${item.bandId}|${item.dateKey ?? ""}|${item.stageId ?? ""}`
        )
      ),
    [visibleStagedBookings]
  );

  const hiddenStagedCount = stagedBookings.length - visibleStagedBookings.length;

  const existingAnnouncementBands = useMemo(() => {
    const nameLookup = new Map(allBands.map((band) => [band.id, band.name]));

    return Array.from(existingBandIds)
      .map((bandId) => ({
        id: bandId,
        name: nameLookup.get(bandId) ?? `Unknown band (${bandId})`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allBands, existingBandIds]);

  const handleToggleBand = (bandId: string) => {
    setSelectedBandIds((prev) => {
      const next = new Set(prev);

      if (next.has(bandId)) {
        next.delete(bandId);
      } else {
        next.add(bandId);
      }

      return next;
    });
  };

  const handleSelectVisibleBands = () => {
    setSelectedBandIds((prev) => {
      const next = new Set(prev);

      filteredBands.forEach((band) => {
        if (existingBandIds.has(band.id)) return;
        next.add(band.id);
      });

      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedBandIds(new Set());
  };

  const handleOpenSummaryDialog = () => {
    if (!selectedAnnouncementId) {
      toast.error("Select an announcement before staging bands.");
      return;
    }

    if (selectedBands.length === 0) {
      toast.error("Select at least one band to stage.");
      return;
    }

    setShowSummaryDialog(true);
  };

  const handleConfirmStage = () => {
    if (!selectedAnnouncement) return;

    const dateKey = selectedDate ? selectedDate.format("YYYY/MM/DD") : null;
    const stageId = selectedStageId || null;
    const stageName = selectedStageName;
    const nextEntries: StagedBooking[] = [];
    const skippedExisting: string[] = [];
    const skippedDuplicates: string[] = [];

    selectedBands.forEach((band) => {
      if (existingBandIds.has(band.id)) {
        skippedExisting.push(band.name);
        return;
      }

      const key = `${band.id}|${dateKey ?? ""}|${stageId ?? ""}`;
      if (stagedCombinationKeySet.has(key)) {
        skippedDuplicates.push(band.name);
        return;
      }

      nextEntries.push({
        id: `${Date.now()}-${band.id}-${Math.random().toString(36).slice(2, 8)}`,
        announcementId: selectedAnnouncement.id,
        announcementName: selectedAnnouncement.name,
        bandId: band.id,
        bandName: band.name,
        dateKey,
        stageId,
        stageName,
      });
    });

    if (nextEntries.length === 0) {
      toast.error(
        "No new bands were staged. They are already in the announcement or already staged."
      );
      setShowSummaryDialog(false);
      return;
    }

    setStagedBookings((prev) => [...prev, ...nextEntries]);
    setSelectedBandIds(new Set());
    setShowSummaryDialog(false);

    toast.success(`Staged ${nextEntries.length} band${nextEntries.length === 1 ? "" : "s"}.`);

    if (skippedExisting.length > 0) {
      toast.info(
        `${skippedExisting.length} band${
          skippedExisting.length === 1 ? " was" : "s were"
        } already in this announcement and skipped.`
      );
    }

    if (skippedDuplicates.length > 0) {
      toast.info(
        `${skippedDuplicates.length} band${
          skippedDuplicates.length === 1 ? " was" : "s were"
        } already staged with the same date/stage and skipped.`
      );
    }
  };

  const handleClearStagedForAnnouncement = () => {
    setStagedBookings((prev) =>
      prev.filter((item) => item.announcementId !== selectedAnnouncementId)
    );
  };

  const handleRemoveGroup = (group: StagedGroup) => {
    setStagedBookings((prev) =>
      prev.filter(
        (item) =>
          !(
            item.announcementId === selectedAnnouncementId &&
            item.dateKey === group.dateKey &&
            (item.stageId ?? "") === (group.stageId ?? "")
          )
      )
    );
  };

  const handleCreateBookings = async () => {
    if (!selectedAnnouncementId) {
      toast.error("Select an announcement before creating bookings.");
      return;
    }

    if (visibleStagedBookings.length === 0) {
      toast.error("No staged changes to create.");
      return;
    }

    setCreatingBookings(true);

    try {
      const results = await Promise.allSettled(
        visibleStagedBookings.map((item) => {
          const datePayload = buildDatePayload(item.dateKey);

          return createBooking({
            variables: {
              bandId: item.bandId,
              announcementId: selectedAnnouncementId,
              editionId: null,
              stageId: item.stageId ?? null,
              date: datePayload.date,
              startTime: datePayload.startTime,
              endTime: datePayload.endTime,
            },
          });
        })
      );

      const successfulItemIds: string[] = [];
      let failedCount = 0;

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successfulItemIds.push(visibleStagedBookings[index].id);
          return;
        }

        failedCount += 1;
      });

      if (successfulItemIds.length > 0) {
        setStagedBookings((prev) =>
          prev.filter((item) => !successfulItemIds.includes(item.id))
        );
        toast.success(
          `Created ${successfulItemIds.length} booking${
            successfulItemIds.length === 1 ? "" : "s"
          }.`
        );

        if (onBookingsCreated) {
          await onBookingsCreated();
        }
      }

      if (failedCount > 0) {
        toast.error(`${failedCount} booking${failedCount === 1 ? "" : "s"} failed.`);
      }
    } finally {
      setCreatingBookings(false);
    }
  };

  const stagedSummaryNames = selectedBands.map((band) => band.name);
  const summaryDateLabel = selectedDate
    ? `${selectedDate.format("YYYY/MM/DD")} ${selectedDate.format("dddd")}`
    : "no specific date";
  const summaryStageLabel = selectedStageName ? `"${selectedStageName}"` : "no specific stage";
  const previewNameLimit = 10;
  const previewNames = stagedSummaryNames.slice(0, previewNameLimit).join(", ");
  const remainingNames = Math.max(stagedSummaryNames.length - previewNameLimit, 0);

  return (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Booking Builder Controls
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="booking-builder-announcement-label">
                  Announcement
                </InputLabel>
                <Select
                  labelId="booking-builder-announcement-label"
                  label="Announcement"
                  value={selectedAnnouncementId}
                  onChange={(event) => setSelectedAnnouncementId(event.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>
                      {announcements.length === 0
                        ? "No announcements available"
                        : "Select an announcement"}
                    </em>
                  </MenuItem>
                  {announcements.map((announcement) => (
                    <MenuItem key={announcement.id} value={announcement.id}>
                      {announcement.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Date (optional)"
                value={selectedDate}
                onChange={(value) => setSelectedDate(value)}
                format="YYYY/MM/DD"
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />

              <FormControl fullWidth>
                <InputLabel id="booking-builder-stage-label">Stage (optional)</InputLabel>
                <Select
                  labelId="booking-builder-stage-label"
                  label="Stage (optional)"
                  value={selectedStageId}
                  onChange={(event) => setSelectedStageId(event.target.value)}
                >
                  <MenuItem value="">
                    <em>No stage</em>
                  </MenuItem>
                  {stageOptions.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={1}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">
                  Bands already in announcement
                </Typography>
                <Chip size="small" label={existingAnnouncementBands.length} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {selectedAnnouncement ? (
                existingAnnouncementBands.length > 0 ? (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      maxHeight: 260,
                      overflowY: "auto",
                    }}
                  >
                    {existingAnnouncementBands.map((band, index) => (
                      <Box key={band.id}>
                        <ListItemText sx={{ px: 2, py: 0.75 }} primary={band.name} />
                        {index < existingAnnouncementBands.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No bands are currently in this announcement.
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select an announcement to view existing bands.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
        <Card elevation={1} sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Typography variant="h6">Bands</Typography>
                <Chip
                  size="small"
                  label={`${selectedBandIds.size} selected`}
                  color={selectedBandIds.size > 0 ? "primary" : "default"}
                />
              </Stack>

              <TextField
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search bands"
                label="Search bands"
                fullWidth
              />

              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={handleSelectVisibleBands}>
                  Select visible
                </Button>
                <Button size="small" onClick={handleClearSelection}>
                  Clear selection
                </Button>
              </Stack>

              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 440,
                  overflowY: "auto",
                }}
              >
                {loadingBands ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Loading bands...
                  </Typography>
                ) : filteredBands.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No bands match your search.
                  </Typography>
                ) : (
                  filteredBands.map((band, index) => {
                    const selected = selectedBandIds.has(band.id);
                    const alreadyInAnnouncement = existingBandIds.has(band.id);

                    return (
                      <Box key={band.id}>
                        <ListItemButton
                          disabled={alreadyInAnnouncement}
                          onClick={() => handleToggleBand(band.id)}
                        >
                          <Checkbox checked={selected} />
                          <ListItemText
                            primary={band.name}
                            secondary={
                              alreadyInAnnouncement
                                ? "Already exists in selected announcement"
                                : undefined
                            }
                          />
                        </ListItemButton>
                        {index < filteredBands.length - 1 && <Divider />}
                      </Box>
                    );
                  })
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleOpenSummaryDialog}
                disabled={selectedBandIds.size === 0 || !selectedAnnouncementId}
              >
                Stage Selected Changes
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={1} sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Typography variant="h6">Staged Changes</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={handleClearStagedForAnnouncement}
                    disabled={!selectedAnnouncementId || visibleStagedBookings.length === 0}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCreateBookings}
                    disabled={
                      creatingBookings ||
                      !selectedAnnouncementId ||
                      visibleStagedBookings.length === 0
                    }
                  >
                    {creatingBookings
                      ? "Creating..."
                      : `Create Bookings (${visibleStagedBookings.length})`}
                  </Button>
                </Stack>
              </Stack>

              {hiddenStagedCount > 0 && selectedAnnouncement ? (
                <Typography variant="caption" color="text.secondary">
                  {hiddenStagedCount} staged booking
                  {hiddenStagedCount === 1 ? "" : "s"} belong to other announcements.
                </Typography>
              ) : null}

              {!selectedAnnouncement ? (
                <Typography variant="body2" color="text.secondary">
                  Select an announcement to view staged changes.
                </Typography>
              ) : groupedStagedBookings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No staged changes yet.
                </Typography>
              ) : (
                groupedStagedBookings.map((group) => (
                  <Box
                    key={group.key}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1.5,
                      p: 2,
                      backgroundColor: "rgba(0,0,0,0.01)",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                      >
                        <Typography variant="subtitle2">
                          {formatGroupHeading(group)}
                        </Typography>
                        <Button size="small" onClick={() => handleRemoveGroup(group)}>
                          Remove group
                        </Button>
                      </Stack>

                      <Stack spacing={0.5}>
                        {group.bands.map((item) => (
                          <Typography key={item.id} variant="body2">
                            {item.bandName}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm staged changes</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body1">
              You have added {previewNames}
              {remainingNames > 0 ? `, and ${remainingNames} more` : ""} band
              {stagedSummaryNames.length === 1 ? "" : "s"} to appear on {summaryDateLabel} at{" "}
              {summaryStageLabel}.
            </Typography>

            {selectedAnnouncement ? (
              <Typography variant="body2" color="text.secondary">
                Announcement: {selectedAnnouncement.name}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummaryDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmStage}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default BookingBuilder;
